import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages } from 'react-intl'
import classnames from 'classnames'

// Components
import Player from 'src/Components/shared/Player'
import Text from 'src/Components/shared/Text'
import Question from 'src/Components/shared/Questions/QuestionSingleContainer'
import HomeSalesSvg from 'src/Components/shared/EstimatedValue/Chart/Warnings/shared/HomeSalesSvg'
import { Button, Page } from 'src/Components'

// Selectors
import {
  selectCustomerFirstName,
  selectCustomerPhotoUri,
} from 'src/store/selectors/customerProfile'
import { selectClientFetchId } from 'src/store/selectors/client'
import { selectHomeId } from 'src/store/selectors/home'

// Utils
import { SYSTEM_REQUEST } from 'src/constants/messages'
import { HOME_VALUE_TOPIC_KEY } from 'src/constants/topicKeys'
import { useFormatMessage } from 'src/providers/intl'
import { PATH_HELPERS } from 'src/constants/navigation'
import {
  AVMDifferenceLearnMoreLoad,
  AvmDifferenceTuneValue,
} from 'src/constants/trackingEvents'
import { dispatchEvent } from 'src/helpers/tagManager'
import history from 'src/history'
import FirstReasonSvg from './FirstReasonSvg'

// Styles
import styles from './AvmDifferenceWarning.module.scss'

const message = defineMessages({
  placeholder: {
    id: 'Homeowner.avmDiffWarning.footer.questionPlaceHolder',
    defaultMessage: 'Can you verify my home value?',
  },
})

const AvmDifferenceWarning = () => {
  const avatarUri = useSelector(selectCustomerPhotoUri)
  const clientId = useSelector(selectClientFetchId)
  const firstName = useSelector(selectCustomerFirstName)
  const homeId = useSelector(selectHomeId)
  const intlFormat = useFormatMessage()

  useEffect(() => {
    dispatchEvent(AVMDifferenceLearnMoreLoad)
  }, [])

  const handleNavigation = () => {
    history.push(PATH_HELPERS.tuneYourValue.buildPath(clientId, homeId))
  }

  return (
    <Page>
      <Page.Body dataQa='avm-warning-learn-more-panel'>
        <section className={styles.section}>
          <Text element='h4' variant='neutralCool' variantCount='900' size='l'>
            <FormattedMessage
              id='Homeowner.avmDiffWarning.title'
              defaultMessage='Home values can jump when extreme data points influence the artificial intelligence'
            />
          </Text>
        </section>
        <section className={styles.section}>
          <Player.wistia videoId='6b04o9immb' title='' />
        </section>
        <section className={styles.section}>
          <div className={styles.wrapper}>
            <div className={styles.row}>
              <div className={styles.column}>
                <Text
                  element='h5'
                  variant='neutralCool'
                  variantCount='900'
                  size='l'
                >
                  <FormattedMessage
                    id='Homeowner.avmDiffWarning.subTitle'
                    defaultMessage='A few reasons this might happen'
                  />
                </Text>
              </div>
            </div>

            <div className={styles.row}>
              <div className={classnames(styles.column, styles.column1)}>
                <div className={styles.firstReason}>
                  <FirstReasonSvg />
                </div>
              </div>

              <div className={classnames(styles.column, styles.column2)}>
                <div>
                  <Text
                    element='p'
                    variant='neutralCool'
                    variantCount='900'
                    size='m'
                  >
                    <FormattedMessage
                      id='Homeowner.avmDiffWarning.firstReason'
                      defaultMessage='Neighborhood changes'
                    />
                  </Text>
                  <Text
                    element='p'
                    variant='neutralCool'
                    variantCount='600'
                    size='s'
                  >
                    <FormattedMessage
                      id='Homeowner.avmDiffWarning.firstReasonExp'
                      defaultMessage='Nearby renovations, construction, or subdividing'
                    />
                  </Text>
                </div>
              </div>
            </div>

            <div className={styles.row}>
              <div className={classnames(styles.column, styles.column1)}>
                <div className={styles.secondReason}>
                  <HomeSalesSvg />
                </div>
              </div>

              <div className={classnames(styles.column, styles.column2)}>
                <div>
                  <Text
                    element='p'
                    variant='neutralCool'
                    variantCount='900'
                    size='m'
                  >
                    <FormattedMessage
                      id='Homeowner.avmDiffWarning.secondReason'
                      defaultMessage='Outlier sales'
                    />
                  </Text>
                  <Text
                    element='p'
                    variant='neutralCool'
                    variantCount='600'
                    size='s'
                  >
                    <FormattedMessage
                      id='Homeowner.avmDiffWarning.secondReasonExp'
                      defaultMessage='Extreme high or low recent sales can weight your value'
                    />
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <Text element='h4' variant='neutralCool' variantCount='900' size='l'>
            <FormattedMessage
              id='Homeowner.avmDiffWarning.footer'
              defaultMessage='Sometimes AI can benefit from a little human intelligence'
            />
          </Text>
          <div className={styles.cta}>
            <Text element='p' variant='neutralCool' variantCount='600' size='s'>
              <FormattedMessage
                id='Homeowner.avmDiffWarning.footerCta'
                defaultMessage='You can {tuneYourValueLink}, or ask {firstName} to help:'
                values={{
                  firstName,
                  tuneYourValueLink: (
                    <Button
                      data-qa='tune-your-value-link'
                      minimal
                      element='a'
                      onClick={handleNavigation}
                      track={AvmDifferenceTuneValue}
                    >
                      <FormattedMessage
                        id='Homeowner.avmDiffWarning.footerCtaTuneLink'
                        defaultMessage='tune your value'
                      />
                    </Button>
                  ),
                }}
              />
            </Text>
          </div>
          <div className={styles.footer}>
            <Question
              questionTopic={intlFormat(
                SYSTEM_REQUEST.avmDifferenceWarningTopic
              )}
              questionTopicKey={HOME_VALUE_TOPIC_KEY}
              placeholder={intlFormat(message.placeholder)}
              avatarUri={avatarUri}
            />
          </div>
        </section>
      </Page.Body>
    </Page>
  )
}

export default AvmDifferenceWarning
